// ═══════════════════════════════════════════════════════════════════
// Aisha Semantic Training — Layer A: Hindi/English synonym dictionary
// ═══════════════════════════════════════════════════════════════════
// Maps colloquial Hindi/Hinglish/regional terms to canonical product
// categories so Aisha can find products even when the customer doesn't
// use the exact site terminology.
//
// Used by AIStyleChatbot to:
// 1. Expand the user query before sending to Gemini (so the system
//    prompt includes canonical terms in productContext matching).
// 2. Inform Gemini of the synonym map so it can map answers back.
//
// Adding new terms: just add to the array. Order doesn't matter.
// Keep terms lowercase. Romanized Hindi ("jhumka") and Devanagari
// ("झुमका") both work.
// ═══════════════════════════════════════════════════════════════════

export const SYNONYMS = [
  // ─── Earrings ──────────────────────────────────────────────
  {
    canonical: 'earrings',
    category:  'jewelry',
    subcategory: 'earrings',
    terms: [
      // English
      'earring', 'earrings', 'ear ring', 'studs', 'stud earrings',
      'drop earrings', 'dangler', 'danglers', 'hoops', 'hoop earrings',
      'chandbali', 'chand bali', 'chandelier earrings',
      // Hinglish / romanized
      'jhumka', 'jhumke', 'jhumki', 'jhumkis', 'kaan ke', 'kaan ki bali',
      'bali', 'baliyan', 'kaanphool', 'kaan phool',
      'kashmari', 'kashmiri jhumka',
      // Devanagari
      'झुमका', 'झुमके', 'झुमकी', 'कान के', 'कान की बाली',
      'बाली', 'बालियां', 'कानफूल',
    ],
  },

  // ─── Necklaces & Sets ───────────────────────────────────────
  {
    canonical: 'necklace',
    category:  'jewelry',
    subcategory: 'necklace',
    terms: [
      'necklace', 'neck piece', 'neckpiece', 'pendant', 'chain',
      'choker', 'collar necklace', 'long necklace', 'rani haar',
      'mala', 'set', 'jewelry set', 'jewellery set',
      // Hinglish
      'haar', 'maala', 'gale ka', 'gale ki', 'kanthi',
      'mangalsutra', 'mangalsutr', 'sita haar',
      // Devanagari
      'हार', 'माला', 'गले का', 'गले की',
      'मंगलसूत्र', 'सीता हार',
    ],
  },

  // ─── Bangles & Chudu ───────────────────────────────────────
  {
    canonical: 'bangles',
    category:  'bangles',
    subcategory: '',
    terms: [
      'bangle', 'bangles', 'bracelet', 'bracelets',
      'kada', 'kade', 'kadas', 'kangan', 'kangna', 'kangans',
      'cuff', 'cuffs', 'wristband',
      // Hinglish - bridal
      'chudi', 'choodi', 'chudiyan', 'choodiyan', 'chura', 'chooda',
      'bridal chura', 'bridal chudiyan', 'lac bangles', 'kundan bangles',
      // Devanagari
      'चूड़ी', 'चूड़ियां', 'कड़ा', 'कंगन', 'चूड़ा',
    ],
  },

  // ─── Hair Accessories ──────────────────────────────────────
  {
    canonical: 'hair accessories',
    category:  'hairAccessories',
    subcategory: '',
    terms: [
      'hair accessory', 'hair clip', 'hair pin', 'hair band',
      'headband', 'tiara', 'crown', 'maang tikka', 'maangtikka',
      'tikka', 'maathapatti', 'matha patti', 'forehead jewelry',
      'jura pin', 'juda pin', 'bun pin',
      'bindi', 'bindis',
      // Hinglish
      'baal ki', 'baal ka', 'choti', 'parandi',
      // Devanagari
      'मांग टीका', 'टीका', 'बिंदी', 'चोटी',
    ],
  },

  // ─── Anklets & Toe Rings ───────────────────────────────────
  {
    canonical: 'anklet',
    category:  'jewelry',
    subcategory: 'anklet',
    terms: [
      'anklet', 'anklets', 'ankle bracelet', 'foot chain',
      'payal', 'payals', 'payjeb', 'paayal',
      'bichuwa', 'toe ring', 'toe rings',
      // Devanagari
      'पायल', 'बिछुआ',
    ],
  },

  // ─── Rings ─────────────────────────────────────────────────
  {
    canonical: 'ring',
    category:  'jewelry',
    subcategory: 'ring',
    terms: [
      'ring', 'rings', 'finger ring', 'cocktail ring',
      'angoothi', 'anguthi', 'mundri',
      'अंगूठी', 'मुंदरी',
    ],
  },

  // ─── Nose Rings ────────────────────────────────────────────
  {
    canonical: 'nose ring',
    category:  'jewelry',
    subcategory: 'nose-ring',
    terms: [
      'nose ring', 'nose pin', 'nath', 'naath', 'nathuni',
      'नथ', 'नथुनी',
    ],
  },

  // ─── Bags & Accessories ────────────────────────────────────
  {
    canonical: 'bags',
    category:  'bags',
    subcategory: '',
    terms: [
      'bag', 'bags', 'handbag', 'purse', 'purses', 'clutch', 'clutches',
      'sling bag', 'tote', 'wallet', 'pouch',
      // Hinglish
      'batua', 'potli', 'jhola',
      // Devanagari
      'बटुआ', 'पोटली', 'झोला', 'थैला',
    ],
  },

  // ─── Sarees ────────────────────────────────────────────────
  {
    canonical: 'saree',
    category:  'sarees',
    subcategory: '',
    terms: [
      'saree', 'sari', 'sarees', 'saris',
      // Hinglish
      'sadi', 'lugada',
      // Devanagari
      'साड़ी', 'साडी',
    ],
  },

  // ─── Garments / Suits / Lehenga ────────────────────────────
  {
    canonical: 'garments',
    category:  'garments',
    subcategory: '',
    terms: [
      'dress', 'suit', 'kurta', 'kurti', 'kurtis',
      'salwar', 'salwar suit', 'lehenga', 'lehnga', 'choli',
      'gown', 'anarkali',
      'blouse', 'top',
      // Devanagari
      'कुर्ता', 'कुर्ती', 'सूट', 'लहंगा', 'गाउन',
    ],
  },

  // ─── Girls / Kids ──────────────────────────────────────────
  {
    canonical: 'girls',
    category:  'girls',
    subcategory: '',
    terms: [
      'girls', 'kids', 'children', 'kid', 'baby',
      'bachhi', 'bachchi', 'beti', 'ladki',
      'बच्ची', 'बेटी', 'लड़की',
    ],
  },
];

// ─────────────────────────────────────────────────────────────
// expandQuery(text) — given user input, return:
//   { matchedCanonicals: ['earrings', ...], categoryHints: ['jewelry'] }
// Used to nudge Gemini toward the right canonical category and to
// surface the synonym map in the system prompt.
// ─────────────────────────────────────────────────────────────
export const expandQuery = (text) => {
  const lower = (text || '').toLowerCase();
  const matchedCanonicals = new Set();
  const categoryHints = new Set();
  const subcategoryHints = new Set();

  for (const entry of SYNONYMS) {
    for (const term of entry.terms) {
      // Match whole word in English/Hinglish OR Devanagari substring
      const isDevanagari = /[\u0900-\u097F]/.test(term);
      if (isDevanagari) {
        if (lower.includes(term)) {
          matchedCanonicals.add(entry.canonical);
          if (entry.category) categoryHints.add(entry.category);
          if (entry.subcategory) subcategoryHints.add(entry.subcategory);
        }
      } else {
        // word boundary match for English/romanized
        const re = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        if (re.test(lower)) {
          matchedCanonicals.add(entry.canonical);
          if (entry.category) categoryHints.add(entry.category);
          if (entry.subcategory) subcategoryHints.add(entry.subcategory);
        }
      }
    }
  }

  return {
    matchedCanonicals: [...matchedCanonicals],
    categoryHints:     [...categoryHints],
    subcategoryHints:  [...subcategoryHints],
  };
};

// ─────────────────────────────────────────────────────────────
// buildSynonymContext() — short reference block injected into
// Gemini system prompt so the model understands customer slang.
// Kept compact to save tokens.
// ─────────────────────────────────────────────────────────────
export const buildSynonymContext = () => {
  return SYNONYMS.map(s => {
    // Keep just 4-6 most common terms per canonical to save tokens
    const sample = s.terms.slice(0, 6).join(', ');
    return `${s.canonical} (${s.category}${s.subcategory ? '/' + s.subcategory : ''}) = ${sample}`;
  }).join('\n');
};
